import { ModeName } from '@commutifi/modes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUndo } from '@fortawesome/free-solid-svg-icons'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { flattenDeep } from 'lodash'
import isEqual from 'lodash/isEqual'
import React, { useState } from 'react'
import {
  Button,
  EditButton,
  CommuteTimeline,
  CommuteTimelineStepDescription,
  TimelineItemProps
} from '@commutifi-fe/ui'
import { LEG_TYPES } from '@commutifi-fe/constants'
import { FormattedMessage } from 'locales/index'
import { TransitOption } from 'shared/components/common/GoogleTransitDirections'
import { useIsMountedRef } from 'shared/hooks/global'
import { useModal } from '@commutifi-fe/hooks'
import { FormItemInjectedProps } from 'shared/interfaces'
import { AddressPoint, AddressPointWithTimezone, Route, RouteLeg } from 'shared/interfaces/Commute'
import { Location } from 'shared/interfaces/Location'
import { getArrayLastValue } from 'utils/index'
import { getTranslationKeyForCategory, getTranslationKeyForMode } from 'utils/modes'
import InterStepModal, { Step, at, atWork } from './components/InterStepModal'
import './styles.module.scss'
import { ModeIcon } from '@commutifi-fe/commutifi-specific/Presentation/ModeIcon'
import { useConfig } from 'shared/providers'

interface EditItemDotProps {
  step: Step
  onEdit: (e: React.MouseEvent) => void
}
type AppButtonProps = React.ComponentProps<typeof Button>

export interface CommuteTimelineProps extends FormItemInjectedProps<Route> {
  startPoint: AddressPoint
  endPoint: AddressPointWithTimezone // (office building potentially has a timezone)
  // Time format should be HHMM (ex. 0800 for 8 am)
  arrivalTime?: string
  isDriveAndTransit?: boolean
  addButtonProps?: AppButtonProps
}

const convertToStepType = (leg: RouteLeg, endLocation: Location | null): Step => ({
  addressPoint: { formattedAddress: leg.endAddress || '', location: leg.endLocation },
  until: endLocation && isEqual(endLocation, leg.endLocation) ? atWork : at,
  transport: { category: leg.category, mode: leg.mode },
  transit: leg.transit
})

const EditItemDotConfig = ({ step, onEdit }: EditItemDotProps): TimelineItemProps => ({
  dot: <ModeIcon mode={step.transport.mode || step.transport.category} />,
  children: (
    <>
      <EditButton
        onEdit={onEdit}
        text={
          <span>
            <FormattedMessage id={getTranslationKeyForCategory(step.transport.category).title} />
            {step.transport.mode && step.transport.mode !== ModeName.Walk ? (
              <span>
                {' - '}
                <FormattedMessage id={getTranslationKeyForMode(step.transport.mode)} />
              </span>
            ) : null}
          </span>
        }
      />
      {step.transit ? (
        <div className="u-panel u-padding--small u-margin-vertical--tiny">
          <TransitOption route={step.transit} />
        </div>
      ) : null}
    </>
  )
})

export function EditableCommuteTimeline({
  startPoint,
  endPoint,
  arrivalTime,
  id,
  onChange,
  value,
  isDriveAndTransit,
  addButtonProps
}: CommuteTimelineProps) {
  const {
    theme: { currentTheme }
  } = useConfig()
  const ref = React.useRef<HTMLButtonElement | null>(null)
  const isMounted = useIsMountedRef()
  const showDriveTransitModal = !isMounted.current && isDriveAndTransit && !value?.legs
  const {
    isOpen: addStepModalVisible,
    openModal: openAddStepModal,
    closeModal: closeAddStepModal
  } = useModal({
    initialIsOpen: Boolean(showDriveTransitModal)
  })
  const [openedEditModalIndex, setOpenedEditModalIndex] = useState<number>(-1)
  const [interSteps, setInterSteps] = useState<Step[]>(
    (value?.legs || []).map((leg) => convertToStepType(leg, endPoint.location))
  )

  const handleAddStep = React.useCallback(() => {
    openAddStepModal()
  }, [openAddStepModal])

  const handleChange = (steps: Step[]) => {
    // Build the route legs based on each steps defined by the user.
    // The output value should comply to the format of a route in our survey
    const legs = steps.map((step, index) => {
      // Always take the previous step as the starting point.
      // Except for the first step where we take the commuter home location
      // which is the starting point of our commute timeline
      // Also, the last step will take the work location (the end point of the timeline)
      const previousStepGeocode = index ? steps[index - 1].addressPoint : startPoint
      return {
        startAddress: previousStepGeocode.formattedAddress,
        startLocation: previousStepGeocode.location,
        endAddress: step.until === atWork ? endPoint.formattedAddress : step.addressPoint.formattedAddress,
        endLocation: step.until === atWork ? endPoint.location : step.addressPoint.location,
        category: step.transport.category,
        mode: step.transport.mode,
        type: LEG_TYPES.leg,
        ...(step.transit && { transit: step.transit })
      }
    })

    if (onChange) {
      onChange({
        startAddress: startPoint.formattedAddress,
        startLocation: startPoint.location,
        endAddress: endPoint.formattedAddress,
        endLocation: endPoint.location,
        legs
      })
    }
  }

  const handleCreateStep = (steps: Step[]) => {
    closeAddStepModal()

    const updatedSteps = [...interSteps, ...steps]
    setInterSteps(updatedSteps)

    // We only call onChange (save) the steps when the user
    // route is completed
    if (steps[steps.length - 1].until === atWork) {
      handleChange(updatedSteps)
    }
    ref.current?.focus()
  }

  const handleSaveStep = (index: number, steps: Step[]) => {
    handleCloseEditModal()
    // Replace edited step and set the new value in our internal state
    const newInterSteps = [...interSteps]
    newInterSteps.splice(index, 1, ...steps)
    setInterSteps(newInterSteps)

    if (interSteps[index + 1]?.transit) {
      const newInterSteps = [...interSteps]
      newInterSteps.splice(index + 1, 1)
      setInterSteps(newInterSteps)
    }

    handleChange(newInterSteps)
    ref.current?.focus()
  }

  const isCommuteIncomplete = React.useCallback(
    () => interSteps.length === 0 || (interSteps.length > 0 && interSteps[interSteps.length - 1].until !== atWork),
    [interSteps]
  )

  const handleEditStep = (index: number) => {
    setOpenedEditModalIndex(index)
  }

  const handleCloseEditModal = () => {
    setOpenedEditModalIndex(-1)
  }

  const handleReset = () => {
    setInterSteps([])
    handleChange([])
  }

  const addNodeButtonProps: AppButtonProps = React.useMemo(
    () =>
      addButtonProps
        ? { ...addButtonProps, onClick: handleAddStep, ref }
        : {
            style: { height: '32px' },
            shape: 'round',
            onClick: handleAddStep,
            className: 'u-flex-justify-start u-flex-align-center',
            icon: <FontAwesomeIcon icon={faPlus} className="u-margin-right--large-3" />
          },
    [addButtonProps, handleAddStep]
  )

  const timelineItems = React.useMemo(
    () =>
      flattenDeep([
        CommuteTimeline.StartNodeConfig({ title: startPoint.formattedAddress }),
        ...interSteps.map((step: Step, index: number) => [
          EditItemDotConfig({ onEdit: () => handleEditStep(index), step }),
          step.until !== atWork
            ? CommuteTimeline.NodeConfig({
                children: (
                  <CommuteTimelineStepDescription
                    title={step.addressPoint.formattedAddress}
                    subtitle={<FormattedMessage id="commuteRoute.timeline.step.description" />}
                  />
                )
              })
            : null
        ]),
        isCommuteIncomplete() ? CommuteTimeline.AddNodeButtonConfig(addNodeButtonProps) : null,
        CommuteTimeline.EndNodeConfig({ title: endPoint.formattedAddress })
      ]).filter(CommuteTimeline.isTimelineItem),
    [startPoint.formattedAddress, interSteps, isCommuteIncomplete, addNodeButtonProps, endPoint.formattedAddress]
  )

  return (
    <div data-testid="commute-timeline">
      {interSteps.length > 0 && (
        <Button
          type="link"
          onClick={handleReset}
          className="u-color--neutral-600 u-font-weight-semi"
          style={{ padding: 0 }}
        >
          <FontAwesomeIcon icon={faUndo} size="sm" />
          <span className="u-margin-left--small">
            <FormattedMessage id="global.startOver" />
          </span>
        </Button>
      )}
      <CommuteTimeline id={id} items={timelineItems} />
      {interSteps.map((step: Step, index: number) => (
        <InterStepModal
          key={`${step.transport.category}-${index}`}
          open={openedEditModalIndex >= 0 && openedEditModalIndex === index}
          onCancel={handleCloseEditModal}
          onSave={(updatedSteps: Step[]) => handleSaveStep(index, updatedSteps)}
          value={step}
          startPoint={startPoint}
          endPoint={endPoint}
          arrivalTime={arrivalTime}
          timezone={endPoint.timezone}
          theme={currentTheme}
        />
      ))}
      <InterStepModal
        key={`add_step_modal-${interSteps.length + 1}`}
        open={addStepModalVisible}
        onCancel={closeAddStepModal}
        onSave={handleCreateStep}
        // Take last point (only if we have a location information) as a start point for the next step
        startPoint={
          getArrayLastValue(interSteps)?.addressPoint ? getArrayLastValue(interSteps).addressPoint : startPoint
        }
        endPoint={endPoint}
        arrivalTime={arrivalTime}
        // Use office timezone
        timezone={endPoint.timezone}
        // We assume most users will have only 2 steps so we set the value of the endpoint
        // to the work office when the user has already one step filled
        initialUntil={interSteps.length ? atWork : undefined}
        // If the user already chose a route we don't want to prefill as drive + transit
        isDriveAndTransit={isDriveAndTransit}
        theme={currentTheme}
      />
    </div>
  )
}

export function CommuteTimelineDriveAndTransit(props: CommuteTimelineProps) {
  return <EditableCommuteTimeline {...props} isDriveAndTransit />
}
